import React, { useState, useEffect } from 'react';
import {
    Box, IconButton, Badge, Popover, Typography, List, ListItem,
    ListItemText, ListItemButton, Divider, Button, Avatar, Chip
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    Clear as ClearIcon,
    Event as EventIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getUserName, isAdmin, getUserId } from '../utils/auth';
import { Refresh as RefreshIcon } from '@mui/icons-material';

function NotificacoesBar() {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificacoes, setNotificacoes] = useState([]);
    const [naoLidas, setNaoLidas] = useState(0);
    const [loading, setLoading] = useState(false);
    const [regenerando, setRegenerando] = useState(false);
    const userName = getUserName() || 'Usuário';
    const userIsAdmin = isAdmin();
    const userId = getUserId();

    const open = Boolean(anchorEl);

    // Carrega notificações
    const loadNotificacoes = async () => {
        try {
            setLoading(true);
            const response = await api.get('/notificacoes');
            setNotificacoes(response.data || []);
            
            // Conta não lidas
            const naoLidasResponse = await api.get('/notificacoes/nao-lidas');
            setNaoLidas(naoLidasResponse.data?.total || 0);
        } catch (error) {
            console.error('Erro ao carregar notificações:', error);
            // Se houver erro, não quebra a aplicação
            setNotificacoes([]);
            setNaoLidas(0);
        } finally {
            setLoading(false);
        }
    };

    // Carrega notificações ao montar e a cada 60 segundos (reduzido de 30s para reduzir requisições)
    useEffect(() => {
        loadNotificacoes();
        const interval = setInterval(loadNotificacoes, 60000); // Atualiza a cada 60s (1 minuto)
        return () => clearInterval(interval);
    }, []);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        loadNotificacoes();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificacaoClick = async (notificacao) => {
        // Marca como lida
        if (!notificacao.lida) {
            try {
                await api.put(`/notificacoes/${notificacao.id}/lida`);
                setNotificacoes(prev => 
                    prev.map(n => n.id === notificacao.id ? { ...n, lida: 1 } : n)
                );
                setNaoLidas(prev => Math.max(0, prev - 1));
            } catch (error) {
                console.error('Erro ao marcar notificação como lida:', error);
            }
        }
        
        // Navega para a edição do prazo específico
        handleClose();
        if (notificacao.prazo_id) {
            // Tenta navegar para o prazo - se não existir, o PrazoForm vai redirecionar para a lista
            navigate(`/prazos/editar/${notificacao.prazo_id}`);
        } else {
            // Fallback: se não tiver prazo_id, vai para a listagem
            navigate('/prazos');
        }
    };

    const handleLimparTodas = async () => {
        try {
            await api.put('/notificacoes/marcar-todas-lidas');
            setNotificacoes(prev => prev.map(n => ({ ...n, lida: 1 })));
            setNaoLidas(0);
        } catch (error) {
            console.error('Erro ao limpar notificações:', error);
        }
    };

    const handleRegenerarNotificacoes = async () => {
        if (!userIsAdmin || !userId) return;
        
        try {
            setRegenerando(true);
            const response = await api.post(`/notificacoes/regenerar/${userId}`);
            alert(`Notificações regeneradas!\n${response.data.message}`);
            // Recarrega as notificações
            await loadNotificacoes();
        } catch (error) {
            console.error('Erro ao regenerar notificações:', error);
            alert('Erro ao regenerar notificações: ' + (error.response?.data?.error || error.message));
        } finally {
            setRegenerando(false);
        }
    };

    const getTipoLabel = (tipo) => {
        const tipos = {
            'VENCENDO_HOJE': 'Vence Hoje',
            'VENCIDO': 'Vencido',
            'PROXIMO': 'Próximo',
            'ATUALIZADO': 'Atualizado',
            'CRIADO': 'Novo Prazo'
        };
        return tipos[tipo] || tipo;
    };

    const getTipoColor = (tipo) => {
        const cores = {
            'VENCENDO_HOJE': 'warning',
            'VENCIDO': 'error',
            'PROXIMO': 'info',
            'ATUALIZADO': 'primary',
            'CRIADO': 'success'
        };
        return cores[tipo] || 'default';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: 'short', 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    return (
        <>
            {/* Avatar e Nome do Usuário */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                <Avatar 
                    sx={{ 
                        width: 40, 
                        height: 40, 
                        bgcolor: 'primary.main',
                        fontSize: '1rem',
                        fontWeight: 'bold'
                    }}
                >
                    {userName.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {userName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                        Online
                    </Typography>
                </Box>
            </Box>

            {/* Botão de Notificações */}
            <Box sx={{ position: 'relative' }}>
                <IconButton
                    color="inherit"
                    onClick={handleClick}
                    sx={{ position: 'relative' }}
                >
                    <NotificationsIcon />
                </IconButton>
                {naoLidas > 0 && (
                    <Badge
                        badgeContent={naoLidas}
                        color="error"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            '& .MuiBadge-badge': {
                                fontSize: '0.7rem',
                                minWidth: '18px',
                                height: '18px',
                                padding: '0 4px'
                            }
                        }}
                    />
                )}
            </Box>

            {/* Popover de Notificações */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: 380,
                        maxWidth: '90vw',
                        maxHeight: 500,
                        mt: 1,
                        borderRadius: 2,
                        boxShadow: (theme) => theme.palette.mode === 'dark' 
                            ? '0 10px 25px rgba(0,0,0,0.3)'
                            : '0 10px 25px rgba(0,0,0,0.15)'
                    }
                }}
            >
                {/* Header */}
                <Box sx={{ 
                    p: 2, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    borderBottom: 1, 
                    borderColor: 'divider',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        Notificações
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {userIsAdmin && (
                            <Button
                                size="small"
                                onClick={handleRegenerarNotificacoes}
                                disabled={regenerando}
                                startIcon={<RefreshIcon />}
                                sx={{ 
                                    textTransform: 'none', 
                                    fontSize: '0.75rem',
                                    minWidth: 'auto',
                                    px: 1,
                                    py: 0.5
                                }}
                            >
                                {regenerando ? 'Regenerando...' : 'Regenerar'}
                            </Button>
                        )}
                        {notificacoes.length > 0 && (
                            <Button
                                size="small"
                                onClick={handleLimparTodas}
                                sx={{ 
                                    textTransform: 'none', 
                                    fontSize: '0.75rem',
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5
                                }}
                            >
                                Limpar
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Lista de Notificações */}
                <List sx={{ maxHeight: 400, overflow: 'auto', p: 0 }}>
                    {loading ? (
                        <ListItem sx={{ py: 3 }}>
                            <ListItemText 
                                primary="Carregando..." 
                                sx={{ textAlign: 'center' }}
                            />
                        </ListItem>
                    ) : notificacoes.length === 0 ? (
                        <ListItem sx={{ py: 4 }}>
                            <ListItemText 
                                primary="Sem notificações"
                                secondary="Você não tem notificações no momento"
                                sx={{ textAlign: 'center' }}
                                primaryTypographyProps={{ color: 'text.secondary' }}
                                secondaryTypographyProps={{ fontSize: '0.75rem' }}
                            />
                        </ListItem>
                    ) : (
                        notificacoes.map((notificacao, index) => (
                            <React.Fragment key={notificacao.id}>
                                <ListItemButton
                                    onClick={() => handleNotificacaoClick(notificacao)}
                                    sx={{
                                        py: 1.5,
                                        px: 2,
                                        opacity: notificacao.lida ? 0.6 : 1,
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': {
                                            bgcolor: (theme) => theme.palette.mode === 'dark' 
                                                ? 'rgba(255,255,255,0.08)' 
                                                : 'rgba(0,0,0,0.04)'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
                                        {/* Indicador de não lida */}
                                        {!notificacao.lida && (
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: 'primary.main',
                                                    mt: 0.5,
                                                    flexShrink: 0
                                                }}
                                            />
                                        )}
                                        {notificacao.lida && (
                                            <Box sx={{ width: 8, flexShrink: 0 }} />
                                        )}
                                        
                                        {/* Conteúdo */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {/* Tipo e Título */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={getTipoLabel(notificacao.tipo)}
                                                    size="small"
                                                    color={getTipoColor(notificacao.tipo)}
                                                    sx={{ 
                                                        height: 20, 
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            </Box>
                                            
                                            {/* Título */}
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: notificacao.lida ? 400 : 600, 
                                                    mb: 0.5,
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {notificacao.titulo}
                                            </Typography>
                                            
                                            {/* Mensagem */}
                                            {notificacao.mensagem && (
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: 'text.secondary', 
                                                        display: 'block', 
                                                        mb: 0.5,
                                                        lineHeight: 1.4
                                                    }}
                                                >
                                                    {notificacao.mensagem}
                                                </Typography>
                                            )}
                                            
                                            {/* Processo */}
                                            {notificacao.processo_numero && (
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: 'text.secondary', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        gap: 0.5,
                                                        mb: 0.5
                                                    }}
                                                >
                                                    <EventIcon sx={{ fontSize: 12 }} />
                                                    {notificacao.processo_numero}
                                                </Typography>
                                            )}
                                            
                                            {/* Data */}
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    color: 'text.secondary', 
                                                    display: 'block', 
                                                    mt: 0.5,
                                                    fontSize: '0.7rem'
                                                }}
                                            >
                                                {formatDate(notificacao.created_at)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </ListItemButton>
                                {index < notificacoes.length - 1 && (
                                    <Divider sx={{ mx: 2 }} />
                                )}
                            </React.Fragment>
                        ))
                    )}
                </List>
            </Popover>
        </>
    );
}

export default NotificacoesBar;

